/** Utilidades compartidas para etiquetar personas con @ en editor y vista de solo lectura. */

export function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Nombre visible por defecto (firstName + apellidos). */
export function defaultDisplayName(u) {
    if (!u) return '';
    return [u.firstName, u.secondName, u.lastName, u.secondSurname]
        .filter(Boolean)
        .join(' ') || 'Usuario';
}

/** Iniciales: primera letra del nombre + primera del primer apellido. */
export function userInitials(u) {
    if (!u) return '?';
    const first = String(u.firstName || u.secondName || '').trim();
    const last = String(u.lastName || u.secondSurname || '').trim();
    const a = first.charAt(0);
    const b = last.charAt(0);
    if (a && b) return `${a}${b}`.toUpperCase();
    if (a) return a.toUpperCase();
    const fallback = String(u.userName || u.email || '?').trim();
    return (fallback.slice(0, 2) || '?').toUpperCase();
}

/** Filtro por defecto en notas de estudiante: solo personal administrativo. */
export function isStaffMentionable(u) {
    if (!u) return false;
    const id = u._id ?? u.id;
    if (!id) return false;
    const t = u.typeUser;
    if (t === 'estudiante' || t === 'profesor') return false;
    return true;
}

/** Excluye al usuario actual de la lista de menciones (p. ej. chat). */
export function excludeUserId(currentUserId) {
    const selfId = currentUserId != null ? String(currentUserId) : null;
    return (u) => {
        const id = String(u?._id ?? u?.id ?? '');
        return Boolean(id) && id !== selfId;
    };
}

export function buildUserMap(userList, taggedUserDocs = []) {
    const map = new Map();
    for (const u of userList || []) {
        const id = String(u?._id ?? u?.id ?? '');
        if (id) map.set(id, u);
    }
    for (const u of taggedUserDocs || []) {
        const id = String(u?._id ?? u?.id ?? '');
        if (id && !map.has(id)) map.set(id, u);
    }
    return map;
}

export function normalizeTaggedUserIds(taggedUsersField = []) {
    const ids = [];
    const docs = [];
    for (const u of taggedUsersField || []) {
        if (u != null && typeof u === 'object' && (u._id != null || u.id != null)) {
            docs.push(u);
            ids.push(String(u._id ?? u.id));
        } else if (u != null && u !== '') {
            ids.push(String(u));
        }
    }
    return { ids, docs };
}

export function createMentionChip(id, label) {
    const span = document.createElement('span');
    span.setAttribute('data-mention-id', id);
    span.setAttribute('contenteditable', 'false');
    span.className = 'mention-chip';

    const nameEl = document.createElement('span');
    nameEl.className = 'mention-chip__label';
    nameEl.textContent = label;

    span.appendChild(nameEl);
    return span;
}

function splitTextToNodes(text) {
    const out = [];
    const parts = String(text ?? '').split('\n');
    parts.forEach((p, i) => {
        if (i > 0) out.push(document.createElement('br'));
        if (p.length) out.push(document.createTextNode(p));
    });
    return out;
}

export function buildEditorChildren(text, taggedUserIds, userMap, nameOf = defaultDisplayName) {
    const names = [];
    const seen = new Set();
    for (const tid of taggedUserIds || []) {
        const id = String(tid);
        if (seen.has(id)) continue;
        seen.add(id);
        const u = userMap.get(id);
        if (u) names.push({ id, name: nameOf(u) });
    }
    names.sort((a, b) => b.name.length - a.name.length);

    const str = String(text ?? '');
    if (!names.length) return splitTextToNodes(str);

    const alts = names.map((n) => escapeRegExp(`@${n.name}`)).join('|');
    const re = new RegExp(`(${alts})(?=$|\\s|\\n)`, 'g');

    const out = [];
    let last = 0;
    let m;
    while ((m = re.exec(str)) !== null) {
        if (m.index > last) {
            out.push(...splitTextToNodes(str.slice(last, m.index)));
        }
        const matchedName = m[1].slice(1);
        const found = names.find((n) => n.name === matchedName);
        if (found) {
            out.push(createMentionChip(found.id, m[1]));
        } else {
            out.push(...splitTextToNodes(m[1]));
        }
        last = m.index + m[1].length;
    }
    if (last < str.length) {
        out.push(...splitTextToNodes(str.slice(last)));
    }
    return out;
}

/** Recorre el editor y reconstruye texto plano + ids de menciones. */
export function serializeEditor(root) {
    let text = '';
    const ids = [];
    const visit = (nodes) => {
        nodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                text += child.nodeValue || '';
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                if (child.tagName === 'BR') {
                    text += '\n';
                } else if (child.dataset && child.dataset.mentionId) {
                    const labelEl = child.querySelector('.mention-chip__label');
                    text += labelEl?.textContent || child.textContent || '';
                    ids.push(child.dataset.mentionId);
                } else if (child.tagName === 'DIV') {
                    if (text.length > 0 && !text.endsWith('\n')) text += '\n';
                    visit([...child.childNodes]);
                } else {
                    visit([...child.childNodes]);
                }
            }
        });
    };
    visit([...root.childNodes]);
    return { text, ids };
}

export function getActiveMentionInEditor(root) {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return null;
    const node = range.startContainer;
    if (!root.contains(node)) return null;
    if (node.nodeType !== Node.TEXT_NODE) return null;
    const text = node.nodeValue || '';
    const offset = range.startOffset;
    const before = text.slice(0, offset);
    const at = before.lastIndexOf('@');
    if (at < 0) return null;
    if (at > 0) {
        if (!/\s/.test(before[at - 1])) return null;
    } else {
        const prev = node.previousSibling;
        if (prev && prev.nodeType === Node.ELEMENT_NODE && prev.tagName !== 'BR') {
            return null;
        }
    }
    const query = before.slice(at + 1);
    if (!/^[^\s@]*$/.test(query)) return null;
    return { node, atOffset: at, query };
}

export function filterMentionCandidates(userList, query, filterFn, nameOf) {
    const q = (query || '').trim().toLowerCase();
    const base = (userList || []).filter(filterFn);
    if (!q) return base.slice(0, 40);
    return base
        .filter((u) => {
            const name = nameOf(u).toLowerCase();
            const mail = String(u.email || '').toLowerCase();
            const un = String(u.userName || '').toLowerCase();
            return name.includes(q) || mail.includes(q) || un.includes(q);
        })
        .slice(0, 40);
}

export function insertMentionAtActive(ctx, user, nameOf) {
    const { node, atOffset, query } = ctx;
    const text = node.nodeValue || '';
    const before = text.slice(0, atOffset);
    const after = text.slice(atOffset + 1 + query.length);
    const parent = node.parentNode;
    if (!parent) return;

    const beforeNode = before.length ? document.createTextNode(before) : null;
    const chip = createMentionChip(String(user._id ?? user.id), `@${nameOf(user)}`);
    const afterNode = document.createTextNode(`\u00A0${after}`);

    if (beforeNode) parent.insertBefore(beforeNode, node);
    parent.insertBefore(chip, node);
    parent.insertBefore(afterNode, node);
    parent.removeChild(node);

    const sel = window.getSelection();
    const range = document.createRange();
    range.setStart(afterNode, 1);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
}
